import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import Paper from '@base/Paper/Paper';
import { useEffect, useState } from 'react';
import H3 from '@base/H3/H3';
import { breakpoints, mediaQuery, Spacings } from '@styles/index';
import { getDoubtSolutionsHomeTags } from '@utils/breadcrumbs';
import Breadcrumbs from '@cont/CatalogueItemDetails/BreadCrumbs';
import Button from '@base/Button/Button';
import RadioList from '@comp/RadioList/RadioList';
import DrawerFooter from '@cont/Doubts20/DrawerFooter';
import { DOW2_DOUBT_LANGUAGE_SELECTED } from '@constants/segment';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import useDoubtConfig from '@api/hooks/goal/custom-doubts/useDoubtConfig';
import { saveLanguagePreference } from '@api/fetchers/doubts-and-solutions';
import useSubjectAndLanguages from '@api/hooks/goal/custom-doubts/useSubjectAndLanguages';

const StyledPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    box-shadow: none;
    background-color: var(--color-base-0);
    padding: ${Spacings.SPACING_6B} 0;
  }
`;

const LanguageButton = styled(Button)`
  ${mediaQuery.uptoMobile} {
    padding: 0 ${Spacings.SPACING_2B};
  }
`;

const RowDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const StyBreadCrumbs = styled(Breadcrumbs)`
  margin-bottom: ${Spacings.SPACING_6B};
`;

const StyH3 = styled(H3)`
  color: var(--color-text-primary);
`;

const TopBlock = ({ goalUID, className }) => {
  const { goalInfo: goalData } = useGoalInfo(goalUID);
  const [languageDrawer, setLanguageDrawer] = useState(false);
  const routeGoalSegmentData = useGoalEventProps({ goalUID });

  const {
    doubtConfigData: { preferredLanguage: selectedDoubtLanguage, isASDEnabled },
    mutate: refetchConfig
  } = useDoubtConfig(goalUID);
  const { subjectAndLanguageData } = useSubjectAndLanguages(goalUID);
  const { languages = [] } = subjectAndLanguageData;
  const [selectedRadio, setSelectedRadio] = useState();
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (selectedDoubtLanguage?.id) setSelectedRadio(selectedDoubtLanguage?.id);
  }, [selectedDoubtLanguage?.id]);

  const onLanguageClick = () => {
    setLanguageDrawer(true);
  };
  const isFirstTimeLanguageSelection = !selectedDoubtLanguage?.id;
  const onFooterNextClick = () => {
    if (!selectedRadio) {
      setErrorMsg('Select a language to continue');
      return;
    }
    saveLanguagePreference(goalUID, selectedRadio).then((res) => {
      if (!res.error) {
        const selectedLangauge =
          languages?.filter((lang) => lang.id === selectedRadio)?.[0] || '';
        segment.track(DOW2_DOUBT_LANGUAGE_SELECTED, {
          doubt_type: 'AAD',
          feature_version: 2,
          flow_type: 'Non_Demo',
          is_first_time: isFirstTimeLanguageSelection,
          doubt_language_preference: selectedLangauge?.name,
          ...routeGoalSegmentData
        });
        refetchConfig();
        setLanguageDrawer(false);
      } else {
        setErrorMsg('Something went wrong.');
      }
    });
  };

  const getFooter = () => {
    return (
      <DrawerFooter
        errorMsg={errorMsg}
        onNextClick={onFooterNextClick}
        buttonText="Save"
        buttonWidth="136px"
      />
    );
  };

  return (
    <>
      <StyledPaper
        className={className}
        gridColumn="span 18"
        padding={`${Spacings.SPACING_10B} ${Spacings.SPACING_12B}`}
      >
        <StyBreadCrumbs
          goalData={goalData}
          getTags={getDoubtSolutionsHomeTags}
        />
        <RowDiv>
          <StyH3 as="h1">Doubts & solutions</StyH3>
          {isASDEnabled ? (
            <LanguageButton
              grid=""
              variant="hollow"
              color="black"
              label="Language preference"
              width="173px"
              onClick={onLanguageClick}
            />
          ) : null}
        </RowDiv>
      </StyledPaper>
      <RadioList
        isDrawerOpen={languageDrawer}
        mainHeading="Language preference"
        subHeading="Solution language preference for your doubts"
        list={languages.slice(1)}
        selectedItemUID={selectedRadio}
        onChange={setSelectedRadio}
        footer={getFooter()}
        needFooter
        openDrawer={setLanguageDrawer}
        hideCloseButton={false}
        color="var(--color-text-primary)"
      />
    </>
  );
};

export default TopBlock;

TopBlock.propTypes = {
  className: PropTypes.string,
  goalUID: PropTypes.string.isRequired
};

TopBlock.defaultProps = {
  className: ''
};
